import React, { FC } from 'react'
import { Box, Flex } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'

import ErrorMessage from 'front-end/src/components/common/error/ErrorMessage'
import {
  formatDateLongForCert,
  formatDateShort
} from 'front-end/src/utils/dateHelpers'

import IconPencil from '../../components/common/icon/IconPencil'
import PageHeader from '../../components/common/page/header/PageHeader'
import Table, { TableBody } from '../../components/common/table/Table'
import { Certificate } from './Certificate'
import { CertificationEnrolmentCard } from '../certifications-page/components/CertificationEnrolmentCard'
import { CertificationItem } from './CertificationItem'
import { ICertificationsManagePageProps } from '../certifications-page/manage/ManageCertificationPage'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import { Page, PagePanel } from '../../components/common/page/Page'
import { useCertificationEnrolmentQuery } from './CertificationPage.codegen'
import { ButtonOutline } from 'front-end/src/components'
import { brandColors } from 'shared/basicPalette'
import { AdminOnlyAllowed } from '../../components/common/AdminOnlyAllowed'
import { useParams } from 'react-router'

export const CertificationPage: FC<ICertificationsManagePageProps> = () => {
  const params = useParams<{
    certificationId: string
  }>()
  const certificationId = parseInt(params.certificationId)

  const { data } = useCertificationEnrolmentQuery({
    variables: {
      certificationId
    },
    fetchPolicy: 'network-only'
  })

  if (!data) {
    return null
  }
  const certificationEnrolment = data.me.certificationEnrolment

  if (!certificationEnrolment) {
    return (
      <ErrorMessage>
        <Trans>You have no such certification</Trans>
      </ErrorMessage>
    )
  }
  const certification = certificationEnrolment?.certification
  const awardedDate = formatDateShort(certificationEnrolment?.completed_at)
  const expiryDate = formatDateShort(certificationEnrolment?.expires_at)
  const awardedDateLong = formatDateLongForCert(
    certificationEnrolment?.completed_at
  )
  const expiryDateLong = formatDateLongForCert(
    certificationEnrolment?.expires_at
  )
  const firstLastName = `${data.me.first_name} ${data.me.last_name}`.trim()

  const variables = {
    organisation:
      certificationEnrolment?.certification.organisation?.name || t`N/A`,
    fullName: data.me.display_name || t`N/A`,
    firstName: data.me.first_name || t`N/A`,
    lastName: data.me.last_name || t`N/A`,
    email: data.me.email || t`N/A`,
    title: certificationEnrolment?.certification.name || t`N/A`,
    authorOfSource:
      certificationEnrolment?.certification.author?.display_name || t`N/A`,
    awardedDate: awardedDate,
    awardedDateLong: awardedDateLong,
    expiryDate: expiryDate,
    expiryDateLong: expiryDateLong,
    firstLastName
  }

  const { svg, orientation } =
    certificationEnrolment.certification?.certificateTemplate ?? {}

  return (
    <Page className="Certifications Certifications--templates --with-heading">
      <Box>
        <PageHeader
          className="PageHeader PageHeader--title-full-width"
          title={
            <CertificationEnrolmentCard
              certificationEnrolment={certificationEnrolment}
              style={{
                width: '100%',
                fontWeight: 400,
                lineHeight: '1.5',
                boxShadow:
                  '0 0 8px 0 rgba(8,16,24,0.03), 0 4px 6px -4px rgba(222,222,222,0.50)',
                _hover: {
                  boxShadow:
                    '0 0 8px 0 rgba(8,16,24,0.03), 0 4px 6px -4px rgba(222,222,222,0.50)'
                }
              }}
            />
          }
          titleRawText={certificationEnrolment.certification.name}
        />
      </Box>
      <PagePanel>
        <Box mt={-16} pt={16} pr={4} pl={4}>
          <Flex pt={0} pb={6} color={brandColors.grey[400]}>
            <Box flexGrow={1}>{certification?.description}</Box>
            <AdminOnlyAllowed allowManagerAccess>
              <Flex ml="16px" mr="16px" display={['none', 'block']}>
                <ButtonOutline
                  onClick={() =>
                    navigateTo(
                      linkBuilder.certificationsManage(
                        certification?.id as number
                      )
                    )
                  }
                  leftIcon={<IconPencil />}
                >
                  <Trans>Edit this certification</Trans>
                </ButtonOutline>
              </Flex>
            </AdminOnlyAllowed>
          </Flex>
          {certificationEnrolment?.completed_at ? (
            svg && (
              <Certificate
                svg={svg}
                variables={variables}
                orientation={orientation}
              />
            )
          ) : (
            <>
              <Box mb={2}>
                <h3>
                  <Trans>Requirements</Trans>
                </h3>
              </Box>
              <Table>
                <TableBody>
                  {certification?.items?.map((item, index) => {
                    return (
                      <CertificationItem
                        key={index}
                        certId={certification.id}
                        item={item}
                      />
                    )
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </Box>
      </PagePanel>
    </Page>
  )
}
